import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import resolveLaunchGameUidFromLocation from '@/lostHooks/gameUid/resolveLaunchGameUidFromLocation';
import useSoftReload, {
  isSoftReloadInProgress,
} from '@/lostHooks/softReload/useSoftReload';
import { SOFT_RELOAD_PRESET } from '@/lostHooks/softReload/softReloadPresets';
import { useStore } from '@/store/useStore';

const normalizeGameUid = (
  gameUid: string | null | undefined,
): string | null => {
  if (!gameUid) {
    return null;
  }

  try {
    return decodeURIComponent(gameUid);
  } catch {
    return gameUid;
  }
};

const launchNewGameReload = {
  preset: SOFT_RELOAD_PRESET.LAUNCH_NEW_GAME,
  hidePopup: true,
} as const;

type ReconcileSource = 'history-pop' | 'bfcache';

const shouldReconcileHistoryGame = (source: ReconcileSource): boolean => {
  if (isSoftReloadInProgress()) {
    return false;
  }

  const historyGameUid = normalizeGameUid(resolveLaunchGameUidFromLocation());
  if (!historyGameUid) {
    return false;
  }

  const {
    userSessionContext: { hasActiveSession },
    gameStatus: { gameLoadSuccess },
    gameContext: { gameUid: loadedGameUid },
  } = useStore.getState();

  if (!hasActiveSession) {
    return false;
  }

  // BFCache restores a frozen JS heap without re-running React effects.
  if (source === 'bfcache') {
    return true;
  }

  if (!gameLoadSuccess || !loadedGameUid) {
    return false;
  }

  return normalizeGameUid(loadedGameUid) !== historyGameUid;
};

/**
 * Reconciles store + iframe with the URL after browser back/forward.
 * - POP: soft reload when the history URL game differs from the loaded game.
 * - BFCache (pageshow persisted): always soft reload — React effects do not re-run.
 * In-app launches (recently played, replace navigation) own their own soft reload.
 */
export const useHistoryGameSync = () => {
  const navigationType = useNavigationType();
  const location = useLocation();
  const { reload } = useSoftReload();
  const reloadRef = useRef(reload);

  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  const isInitialRenderRef = useRef(true);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }

      if (shouldReconcileHistoryGame('bfcache')) {
        reloadRef.current(launchNewGameReload);
      }
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }

    if (navigationType !== 'POP') {
      return;
    }

    if (shouldReconcileHistoryGame('history-pop')) {
      reloadRef.current(launchNewGameReload);
    }
  }, [location.key, navigationType]);
};
